import React from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {useNotifications} from "context/NotificationContext";
import {FormikHelpers} from "formik";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import LessonPlanForm from "pages/LessonPlan/Form";
import {LessonPlanData} from "types/LessonPlan";
import Button from "components/Button";
import {EyeIcon} from "@heroicons/react/solid";

function MutateLessonPlan() {
    const {id} = useParams()
    const {data: lessonPlan, isLoading} = useQuery(
        ['lesson-plans', {id}],
        () => ngage.lessonPlans.get(parseInt(id as string)),
        {enabled: !!id}
    )

    const store = useMutation((data: LessonPlanData) => ngage.lessonPlans.store(data))
    const update = useMutation((data: LessonPlanData) => ngage.lessonPlans.update(parseInt(id as string), data))

    const {notify} = useNotifications()
    const navigate = useNavigate()

    // Handle form submission
    async function handleSubmit(values: LessonPlanData, {setSubmitting}: FormikHelpers<LessonPlanData>) {
        id? await handleUpdate(values) : await handleStore(values)
        await invalidate('lesson-plans.all')

        setSubmitting(false)
        navigate('/lesson-plans')
    }

    // Handle store mutation
    async function handleStore(values: LessonPlanData) {
        try {
            await store.mutateAsync(values)
            notify("success", "Lesson Plan Created", `${values.name} has been created`)
        } catch (error) {
            notify("error", "Lesson Plan Not Created", `${values.name} could not be stored`)
            throw error
        }
    }

    // Handle update mutation
    async function handleUpdate(values: LessonPlanData) {
        try {
            await update.mutateAsync(values)
            notify("success", "Lesson Plan Updated", `${values.name} has been updated`)
            await invalidate(['lesson-plans', {id}])
        } catch (error) {
            notify("error", "Lesson Plan Not Updated", `${values.name} could not be updated`)
            throw error
        }
    }

    // Handle view action
    function handleView() {
        if(id) navigate(`/lesson-plans/${id}`)
    }

    // Prepare breadcrumbs
    const newBreadcrumbs = {'Education': '/', 'Lesson Plans': '/lesson-plans', 'New Lesson Plan': '/lesson-plans/new'}
    const editBreadcrumbs = {'Education': '/', 'Lesson Plans': '/lesson-plans', 'Edit Lesson Plan': `/lesson-plans/${id}/edit`}

    // Prepare actions
    const actions = id? <Button color={Button.colors.PRIMARY} icon={EyeIcon} onClick={handleView}>View</Button> : null

    return (
        <Page title={id ? "Edit Lesson Plan" : "New Lesson Plan"}
              breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
              actions={actions}
              isLoading={isLoading}>
            <LessonPlanForm lessonPlan={lessonPlan} onSubmit={handleSubmit}/>
        </Page>
    );
}

export default MutateLessonPlan;